import React, { createContext, useContext, useState } from "react";

import { useNavigate } from "react-router-dom";
import { GetEncounter, GetPatient, PostEncounter } from "../services/fhir";
import { IEncounterData } from "../utils/interfaces";
import { useFhirData } from "../utils/useFhirData";
import { useAuthContext } from "./AuthContext";
import { useOrganizationContext } from "./OrganizationContext";
import { usePractitionerRoleContext } from "./PractitionerRoleContext";
import { useSessionContext } from "./SessionContext";
import { useSidebarNavigation } from "./SidebarNavigationContext";
import { useLocationContext } from "./LocationContext";
import { currentFeatures, oids } from "@/configs/Settings";
import { GetVetPatient } from "@/services/fhir/vetpatient/GetVetPatient";
import {
  EVOLUTION_NOTE,
  EVOLUTION_TYPE_SERVICE,
} from "@/components/schedule/evolution/constants";
import { isValidSpecialtyText } from "@/utils/resolveAttendanceSpecialty";
import { isTeleconsultoriaParticipant } from "src-next/modules/teleconsultoria/utils/actors";
import { pickHostEncounter } from "src-next/modules/teleconsultoria/utils/teleconsultoriaEncounters";
import {
  resolveTeleconsultorEncounter,
  teleconsultorFirstParticipants,
} from "src-next/modules/teleconsultoria/services/resolveTeleconsultorEncounter";

interface UseClinicalRegisterContextValue {
  verifyEncounter: Function;
  inProgress: any;
  setInProgress: any;
}

export const UseClinicalRegisterContext =
  createContext<UseClinicalRegisterContextValue>(null!);

interface UseClinicalRegisterContextProviderProps {
  children: React.ReactNode;
}

export function ClinicalRegisterContextProvider({
  children,
}: UseClinicalRegisterContextProviderProps) {
  const { user } = useSessionContext();
  const { userPractitionerRole } = usePractitionerRoleContext();
  const { organization } = useOrganizationContext();
  const { setIsSidebarExpanded } = useSidebarNavigation();
  const { location: locationList } = useLocationContext();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { getNewToken, parsedTime } = useAuthContext();
  const [inProgress, setInProgress] = useState("");

  const navigate = useNavigate();

  /**
   * options.asTeleconsultor: abre o Encounter próprio do teleconsultor
   * (partOf = Encounter do host). Sem a flag, fluxo normal do host.
   */
  const verifyEncounter = async (
    dataAppointment: any,
    prescription?: boolean,
    options?: { asTeleconsultor?: boolean }
  ) => {
    // Determina a classe do Encounter a partir do canal de atendimento da Location.
    // Procura o participante do tipo Location no agendamento e busca a location
    // no contexto para ler a extension attendance-channel.
    const locationParticipant = dataAppointment?.participant?.find((p: any) =>
      p?.actor?.reference?.includes("Location")
    );
    const locationRefId = locationParticipant?.actor?.reference?.split("/")?.[1];
    const locationResource = locationList?.find(
      (loc) => loc?.resource?.id === locationRefId
    );
    const resource = locationResource?.resource;
    const channelExt = resource?.extension?.find(
      (ext: any) =>
        ext.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/attendance-channel"
    );
    const attendanceChannel = channelExt?.valueCode as string | undefined;

    // Retrocompatibilidade: locations criadas antes da extension attendance-channel
    // só possuem virtual-service=true. Nesse caso, tratamos como teleatendimento.
    const isLegacyVirtual =
      !attendanceChannel &&
      resource?.extension?.some(
        (ext: any) =>
          ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service" &&
          ext.valueBoolean === true
      );

    // Mapeamento de canal de atendimento para code system FHIR v3-ActCode:
    //   teleatendimento / chat / virtual legado → VR (Virtual)
    //   domicilio                               → HH (Home Health)
    //   presencial / ausente                    → undefined (campo class omitido no Encounter)
    let attendanceClass: string | undefined;
    if (
      attendanceChannel === "teleatendimento" ||
      attendanceChannel === "chat" ||
      isLegacyVirtual
    ) {
      attendanceClass = "VR";
    } else if (attendanceChannel === "domicilio") {
      attendanceClass = "HH";
    }

    // Lê o endereço de domicílio da extension home-visit-address do Appointment,
    // para replicá-lo no Encounter quando o canal for domicílio.
    const homeVisitAddressExt = dataAppointment?.extension?.find((ext: any) =>
      ext.url?.includes("home-visit-address")
    );
    const domicilioAddress: fhir5.Address | undefined =
      homeVisitAddressExt?.valueAddress ?? undefined;

    const appointmentTypeCode =
      dataAppointment?.appointmentType?.coding?.[0]?.code || "";
    // Evolução: mesma note do Appointment no Encounter (só na criação; continue não dá POST)
    const encounterNote =
      appointmentTypeCode === EVOLUTION_TYPE_SERVICE
        ? EVOLUTION_NOTE
        : dataAppointment?.note?.[0]?.text;

    // HCS do host: o do teleconsultor não define a especialidade do Encounter.
    const hcsActor = useFhirData.findParticipant(
      dataAppointment?.participant?.filter(
        (el: any) => !isTeleconsultoriaParticipant(el),
      ),
      "HealthcareService"
    );
    const roleSpecialty =
      userPractitionerRole?.specialty?.[0]?.coding?.[0]?.display;
    // Evolução/PA sem HCS: especialidade do profissional para Encounter.serviceType.
    const specialtyDisplay = isValidSpecialtyText(hcsActor?.display)
      ? hcsActor.display
      : isValidSpecialtyText(roleSpecialty)
        ? roleSpecialty
        : undefined;

    const encounterData: IEncounterData = {
      appointment: dataAppointment?.id || "",
      type: appointmentTypeCode,
      organization: organization?.id || "",
      participant: dataAppointment?.participant,
      attendanceClass,
      // Incluído apenas para agendamentos de domicílio — omitido nos demais
      ...(domicilioAddress ? { domicilioAddress } : {}),
      ...(encounterNote ? { note: encounterNote } : {}),
      ...(specialtyDisplay ? { specialtyDisplay } : {}),
    };

    const patientId = currentFeatures?.defaultApp
      ? useFhirData
          .findParticipant(dataAppointment?.participant, "Patient")
          ?.reference?.split("-")?.[1]
      : useFhirData
          .findParticipant(dataAppointment?.participant, "Patient")
          ?.reference.split("/")?.[1];

    const dataPatient = currentFeatures?.defaultApp
      ? await GetPatient({ identifier: patientId })
      : await GetVetPatient({ id: patientId });
    const patientName = dataPatient?.data?.name?.[0]?.text;

    let encounter: fhir5.Encounter;
    let partOf: string | undefined;
    if (options?.asTeleconsultor) {
      // Teleconsultor: exige o Encounter do host e cria/reaproveita o dele (partOf).
      // Especialidade do próprio teleconsultor quando ele não tem HCS marcado.
      const resolved = await resolveTeleconsultorEncounter({
        appointment: dataAppointment,
        patientId,
        practitionerRef: `Practitioner/${oids.cpf}-${user?.username}`,
        encounterData: {
          ...encounterData,
          specialtyDisplay: isValidSpecialtyText(roleSpecialty)
            ? roleSpecialty
            : undefined,
        },
      });
      if (!resolved) return;
      encounter = resolved.encounter;
      partOf = resolved.hostEncounterId;
    } else {
      // count 20 + pickHostEncounter: ignora o Encounter do teleconsultor (partOf).
      // Sem Encounter de teleconsultor, o resultado é o mesmo do count 1 de antes.
      const verifyEncounter = await GetEncounter({
        patientId,
        appointmentId: dataAppointment?.id,
        lastUpdaded: true,
        count: 20,
      });
      encounter =
        pickHostEncounter(verifyEncounter?.entry) ||
        (await PostEncounter(encounterData).then((response) => response));
    }
    const auxEncounterReference = {
      id: encounter.id,
      subject: patientId,
      requester: {
        reference: `PractitionerRole/${userPractitionerRole?.id}`,
        display: user?.name,
      },
      appointmentId: dataAppointment?.id,
      // Teleconsultor: participants marcados primeiro (ele vira o autor dos registros).
      participant: partOf
        ? teleconsultorFirstParticipants(dataAppointment?.participant)
        : dataAppointment?.participant,
      ...(partOf && { partOf }),
      ...(dataAppointment?.virtualService && {
        virtualService: dataAppointment?.virtualService?.[0]?.addressUrl,
        sessionKey: dataAppointment?.virtualService?.[0]?.sessionKey,
      }),
    };

    setIsSidebarExpanded(false);

    await getNewToken();

    setInProgress(dataAppointment?.id);

    navigate(!prescription ? "/clinicalregister" : "/prescription", {
      state: {
        encounterReference: auxEncounterReference,
        patientId: patientId,
        patientName: patientName,
      },
    });
  };

  const contextValue: UseClinicalRegisterContextValue = {
    verifyEncounter,
    inProgress,
    setInProgress,
  };

  return (
    <UseClinicalRegisterContext.Provider value={contextValue}>
      {children}
    </UseClinicalRegisterContext.Provider>
  );
}

export function useClinicalRegisterContext() {
  return useContext(UseClinicalRegisterContext);
}
